import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase } from './services/supabase';
import type { Session } from '@supabase/supabase-js';

import './styles/article.css';
import { Header } from './components/Header';
import { Footer } from './components/Footer';

// ── Components Configuration ──────────────────────────────────────────────────

// Helper to handle SSR vs CSR component loading
// During SSR (build time), we need static imports to ensure renderToString() captures full HTML.
// During CSR (runtime), we want lazy loading for performance.
const isSSR = typeof window === 'undefined';

// Static imports for SSR/Pre-rendering
import { Landing as LandingSync } from './components/Landing';
import { Pricing as PricingSync } from './components/Pricing';
import { About as AboutSync } from './components/About';
import { Auth as AuthSync } from './components/Auth';
import InsightsListSync from './components/InsightsList';
import InsightPostSync from './components/InsightPost';
import { SupportPage as SupportPageSync } from './components/SupportPage';
import { SupportTicketPage as SupportTicketPageSync } from './components/SupportTicketPage';
import { TermsOfService as TermsOfServiceSync } from './components/TermsOfService';
import { PrivacyPolicy as PrivacyPolicySync } from './components/PrivacyPolicy';
import { RiskDisclaimer as RiskDisclaimerSync } from './components/RiskDisclaimer';
import { FeatureDetail as FeatureDetailSync } from './components/FeatureDetail';
import DashboardSync from './components/Dashboard';
import LiveSignalsSync from './components/LiveSignals';
import StrategiesSync from './components/Strategies';
import { TradeSettings as TradeSettingsSync } from './components/TradeSettings';
import { TradeLog as TradeLogSync } from './components/TradeLog';
import SignalHistorySync from './components/SignalHistory';
import Elite53SignalsSync from './components/Elite53Signals';
import { Profile as ProfileSync } from './components/Profile';
import { AdminDashboard as AdminDashboardSync } from './components/admin/AdminDashboard';
import { AdminUsers as AdminUsersSync } from './components/admin/AdminUsers';
import { AdminSignals as AdminSignalsSync } from './components/admin/AdminSignals';
import { AdminCreateSignal as AdminCreateSignalSync } from './components/admin/AdminCreateSignal';
import { AdminBlockedStrategies as AdminBlockedStrategiesSync } from './components/admin/AdminBlockedStrategies';
import { AdminSupport as AdminSupportSync } from './components/admin/AdminSupport';
import { Cart as CartSync } from './components/Cart';
import { NotFound as NotFoundSync } from './components/NotFound';
import { SupportChat as SupportChatSync } from './components/SupportChat';

// Lazy components for Client
const LandingLazy = React.lazy(() => import('./components/Landing').then(m => ({ default: m.Landing })));
const PricingLazy = React.lazy(() => import('./components/Pricing').then(m => ({ default: m.Pricing })));
const AboutLazy = React.lazy(() => import('./components/About').then(m => ({ default: m.About })));
const AuthLazy = React.lazy(() => import('./components/Auth').then(m => ({ default: m.Auth })));
const InsightsListLazy = React.lazy(() => import('./components/InsightsList'));
const InsightPostLazy = React.lazy(() => import('./components/InsightPost'));
const SupportPageLazy = React.lazy(() => import('./components/SupportPage').then(m => ({ default: m.SupportPage })));
const SupportTicketPageLazy = React.lazy(() => import('./components/SupportTicketPage').then(m => ({ default: m.SupportTicketPage })));
const TermsOfServiceLazy = React.lazy(() => import('./components/TermsOfService').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicyLazy = React.lazy(() => import('./components/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const RiskDisclaimerLazy = React.lazy(() => import('./components/RiskDisclaimer').then(m => ({ default: m.RiskDisclaimer })));
const FeatureDetailLazy = React.lazy(() => import('./components/FeatureDetail').then(m => ({ default: m.FeatureDetail })));
const DashboardLazy = React.lazy(() => import('./components/Dashboard'));
const LiveSignalsLazy = React.lazy(() => import('./components/LiveSignals'));
const StrategiesLazy = React.lazy(() => import('./components/Strategies'));
const TradeSettingsLazy = React.lazy(() => import('./components/TradeSettings').then(m => ({ default: m.TradeSettings })));
const TradeLogLazy = React.lazy(() => import('./components/TradeLog').then(m => ({ default: m.TradeLog })));
const SignalHistoryLazy = React.lazy(() => import('./components/SignalHistory'));
const Elite53SignalsLazy = React.lazy(() => import('./components/Elite53Signals'));
const ProfileLazy = React.lazy(() => import('./components/Profile').then(m => ({ default: m.Profile })));
const AdminDashboardLazy = React.lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUsersLazy = React.lazy(() => import('./components/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminSignalsLazy = React.lazy(() => import('./components/admin/AdminSignals').then(m => ({ default: m.AdminSignals })));
const AdminCreateSignalLazy = React.lazy(() => import('./components/admin/AdminCreateSignal').then(m => ({ default: m.AdminCreateSignal })));
const AdminBlockedStrategiesLazy = React.lazy(() => import('./components/admin/AdminBlockedStrategies').then(m => ({ default: m.AdminBlockedStrategies })));
const AdminSupportLazy = React.lazy(() => import('./components/admin/AdminSupport').then(m => ({ default: m.AdminSupport })));
const CartLazy = React.lazy(() => import('./components/Cart').then(m => ({ default: m.Cart })));
const NotFoundLazy = React.lazy(() => import('./components/NotFound').then(m => ({ default: m.NotFound })));
const SupportChatLazy = React.lazy(() => import('./components/SupportChat').then(m => ({ default: m.SupportChat })));

// Resolved components
const Landing = isSSR ? LandingSync : LandingLazy;
const Pricing = isSSR ? PricingSync : PricingLazy;
const About = isSSR ? AboutSync : AboutLazy;
const Auth = isSSR ? AuthSync : AuthLazy;
const InsightsList = isSSR ? InsightsListSync : InsightsListLazy;
const InsightPost = isSSR ? InsightPostSync : InsightPostLazy;
const SupportPage = isSSR ? SupportPageSync : SupportPageLazy;
const SupportTicketPage = isSSR ? SupportTicketPageSync : SupportTicketPageLazy;
const TermsOfService = isSSR ? TermsOfServiceSync : TermsOfServiceLazy;
const PrivacyPolicy = isSSR ? PrivacyPolicySync : PrivacyPolicyLazy;
const RiskDisclaimer = isSSR ? RiskDisclaimerSync : RiskDisclaimerLazy;
const FeatureDetail = isSSR ? FeatureDetailSync : FeatureDetailLazy;
const Dashboard = isSSR ? DashboardSync : DashboardLazy;
const LiveSignals = isSSR ? LiveSignalsSync : LiveSignalsLazy;
const Strategies = isSSR ? StrategiesSync : StrategiesLazy;
const TradeSettings = isSSR ? TradeSettingsSync : TradeSettingsLazy;
const TradeLog = isSSR ? TradeLogSync : TradeLogLazy;
const SignalHistory = isSSR ? SignalHistorySync : SignalHistoryLazy;
const Elite53Signals = isSSR ? Elite53SignalsSync : Elite53SignalsLazy;
const Profile = isSSR ? ProfileSync : ProfileLazy;
const AdminDashboard = isSSR ? AdminDashboardSync : AdminDashboardLazy;
const AdminUsers = isSSR ? AdminUsersSync : AdminUsersLazy;
const AdminSignals = isSSR ? AdminSignalsSync : AdminSignalsLazy;
const AdminCreateSignal = isSSR ? AdminCreateSignalSync : AdminCreateSignalLazy;
const AdminBlockedStrategies = isSSR ? AdminBlockedStrategiesSync : AdminBlockedStrategiesLazy;
const AdminSupport = isSSR ? AdminSupportSync : AdminSupportLazy;
const Cart = isSSR ? CartSync : CartLazy;
const NotFound = isSSR ? NotFoundSync : NotFoundLazy;
const SupportChat = isSSR ? SupportChatSync : SupportChatLazy;

// ── Helpers ──────────────────────────────────────────────────────────────────

function GA4PageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode; isAdmin?: boolean }> = ({ children, isAdmin }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [userIsAdmin, setUserIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(isSSR ? false : true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) {
        supabase.from('user_profiles')
          .select('is_admin, role')
          .eq('id', session.user.id)
          .single()
          .then(({ data }) => {
            setUserIsAdmin(data?.is_admin || data?.role === 'admin');
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    });
  }, []);

  if (loading) return null;
  if (!session) return <Navigate to="/login" replace />;
  if (isAdmin && !userIsAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(isSSR ? false : true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return null;

  return (
    <>
      <GA4PageTracker />
      <div className="min-h-screen bg-finance-dark flex flex-col">
        <Header />
        <Suspense fallback={null}>
          <SupportChat />
        </Suspense>

        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-finance-darker"><span className="text-white">Loading Page...</span></div>}>
          <Routes>
            {/* Public / Guest Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Navigate to="/insights" replace />} />
            <Route path="/insights" element={<InsightsList />} />
            <Route path="/insights/:slug" element={<InsightPost />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<RiskDisclaimer />} />
            <Route path="/features/:featureId" element={<FeatureDetail />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/ticket/:ticketId" element={<SupportTicketPage />} />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                !isSSR && session && !window.location.hash.includes('type=recovery') && !new URLSearchParams(window.location.search).get('view')?.includes('reset-password')
                  ? <Navigate to="/dashboard" replace />
                  : <Auth />
              }
            />

            {/* Protected / App Routes */}
            <Route
              path="/dashboard"
              element={session ? <Dashboard session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/strategies"
              element={session ? <Strategies session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/settings"
              element={session ? <TradeSettings session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/trades"
              element={session ? <TradeLog session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/signals"
              element={session ? <LiveSignals session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/signal-history"
              element={session ? <SignalHistory session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/elite-signals"
              element={session ? <Elite53Signals session={session} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/profile"
              element={session ? <Profile session={session} /> : <Navigate to="/login" replace />}
            />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute isAdmin><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute isAdmin><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/signals" element={<ProtectedRoute isAdmin><AdminSignals /></ProtectedRoute>} />
            <Route path="/admin/create-signal" element={<ProtectedRoute isAdmin><AdminCreateSignal /></ProtectedRoute>} />
            <Route path="/admin/strategies" element={<ProtectedRoute isAdmin><AdminBlockedStrategies /></ProtectedRoute>} />
            <Route path="/admin/support" element={<ProtectedRoute isAdmin><AdminSupport /></ProtectedRoute>} />

            {/* Catch all */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <Footer />
      </div>
    </>
  );
};

export default App;